/**
 * Prekey Management Service
 * Handles prekey bundle generation, storage, and retrieval
 */

import { supabase } from '../supabase';
import { generatePrekeyBundle, PrekeyBundle } from './prekeys';
import { debug } from '../../../lib/utils/debug';
import nacl from 'tweetnacl';
import * as naclUtil from 'tweetnacl-util';

/**
 * Generate and upload prekey bundle for a user
 * @param identityKeyPair Ed25519 signing key pair (for identity key)
 */
export async function uploadPrekeyBundle(
  userId: string,
  identityKeyPair: nacl.SignKeyPair,
  numOneTimePrekeys: number = 100
): Promise<void> {
  try {
    // Generate prekey bundle
    const bundle = generatePrekeyBundle(identityKeyPair, numOneTimePrekeys);
    
    // Serialize signed prekey data
    const signedPrekeyData = {
      keyId: bundle.signedPrekey.keyId,
      publicKey: naclUtil.encodeBase64(bundle.signedPrekey.publicKey),
      signature: naclUtil.encodeBase64(bundle.signedPrekey.signature)
    };
    
    // Update user's signed prekey and public key
    const { error: updateError } = await supabase
      .from('users')
      .update({
        public_key: naclUtil.encodeBase64(identityKeyPair.publicKey), // Store Ed25519 public key
        signed_prekey_id: bundle.signedPrekey.keyId,
        signed_prekey: signedPrekeyData.publicKey,
        signed_prekey_signature: signedPrekeyData.signature,
        prekey_bundle_updated_at: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (updateError) {
      debug.error('Error updating signed prekey:', updateError);
      throw updateError;
    }
    
    // Delete old unused prekeys
    await supabase
      .from('prekeys')
      .delete()
      .eq('user_id', userId)
      .is('used_at', null);
    
    // Insert new one-time prekeys
    const prekeysToInsert = bundle.oneTimePrekeys.map(prekey => ({
      user_id: userId,
      key_id: prekey.keyId,
      public_key: naclUtil.encodeBase64(prekey.publicKey)
    }));
    
    // Insert in batches (Supabase has limits)
    const batchSize = 50;
    for (let i = 0; i < prekeysToInsert.length; i += batchSize) {
      const batch = prekeysToInsert.slice(i, i + batchSize);
      const { error: insertError } = await supabase
        .from('prekeys')
        .insert(batch);
      
      if (insertError) {
        debug.error('Error inserting prekeys:', insertError);
        throw insertError;
      }
    }
    
    debug.log(`Uploaded prekey bundle for user ${userId}: ${prekeysToInsert.length} one-time prekeys`);
  } catch (error) {
    debug.error('Error uploading prekey bundle:', error);
    throw error;
  }
}

/**
 * Fetch prekey bundle for a user
 */
export async function fetchPrekeyBundle(userId: string): Promise<PrekeyBundle | null> {
  try {
    // Fetch user's identity key and signed prekey
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('public_key, signed_prekey_id, signed_prekey, signed_prekey_signature')
      .eq('id', userId)
      .single();
    
    if (userError || !userData) {
      debug.error('Error fetching user prekey data:', userError);
      return null;
    }
    
    if (!userData.public_key || !userData.signed_prekey) {
      debug.warn(`User ${userId} does not have prekey bundle`);
      return null;
    }
    
    // Fetch one unused one-time prekey
    const { data: prekeyData } = await supabase
      .from('prekeys')
      .select('key_id, public_key')
      .eq('user_id', userId)
      .is('used_at', null)
      .limit(1)
      .single();
    
    // It's okay if there's no one-time prekey (signed prekey is sufficient)
    const oneTimePrekey = prekeyData ? {
      keyId: prekeyData.key_id,
      publicKey: naclUtil.decodeBase64(prekeyData.public_key)
    } : undefined;
    
    // Construct prekey bundle
    const bundle: PrekeyBundle = {
      identityKey: naclUtil.decodeBase64(userData.public_key),
      signedPrekey: {
        keyId: userData.signed_prekey_id,
        publicKey: naclUtil.decodeBase64(userData.signed_prekey),
        signature: naclUtil.decodeBase64(userData.signed_prekey_signature)
      },
      oneTimePrekeys: oneTimePrekey ? [oneTimePrekey] : []
    };
    
    return bundle;
  } catch (error) {
    debug.error('Error fetching prekey bundle:', error);
    return null;
  }
}

/**
 * Mark a one-time prekey as used
 */
export async function markPrekeyUsed(userId: string, keyId: number): Promise<void> {
  try {
    const { error } = await supabase
      .from('prekeys')
      .update({ used_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('key_id', keyId);
    
    if (error) {
      debug.error('Error marking prekey as used:', error);
      // Don't throw - this is not critical
    }
  } catch (error) {
    debug.error('Error marking prekey as used:', error);
  }
}

/**
 * Check if user needs to regenerate prekey bundle
 * Regenerates if:
 * - No prekey bundle exists
 * - Less than 10 one-time prekeys remaining
 * - Bundle is older than 30 days
 * @param identityKeyPair Ed25519 signing key pair (for identity key)
 */
export async function ensurePrekeyBundle(
  userId: string,
  identityKeyPair: nacl.SignKeyPair
): Promise<void> {
  try {
    // Validate key sizes before proceeding
    if (identityKeyPair.publicKey.length !== 32 || identityKeyPair.secretKey.length !== 64) {
      throw new Error(`Invalid identity key sizes: publicKey=${identityKeyPair.publicKey.length} bytes, secretKey=${identityKeyPair.secretKey.length} bytes. Expected: publicKey=32, secretKey=64`);
    }
    
    // Check if user has prekey bundle
    // Select all relevant fields, including signed_prekey to check if bundle exists
    const { data: userData, error: userFetchError } = await supabase
      .from('users')
      .select('prekey_bundle_updated_at, signed_prekey_id, signed_prekey, signed_prekey_signature')
      .eq('id', userId)
      .single();
    
    // Handle database errors gracefully
    if (userFetchError) {
      // If column doesn't exist (PGRST116, 42703) or 400 Bad Request (column missing), generate bundle anyway
      if (userFetchError.code === 'PGRST116' || userFetchError.code === '42703' || 
          userFetchError.message?.includes('column') || 
          userFetchError.message?.includes('does not exist')) {
        debug.warn(`Prekey columns may not exist yet, generating bundle anyway: ${userFetchError.message}`);
        await uploadPrekeyBundle(userId, identityKeyPair);
        return;
      }
      // For 400 Bad Request (likely missing column), try to generate bundle
      if (!userFetchError.code || userFetchError.code === '400' || userFetchError.message?.includes('Bad Request')) {
        debug.warn(`Database query failed (possibly missing columns), generating bundle anyway: ${userFetchError.message}`);
        await uploadPrekeyBundle(userId, identityKeyPair);
        return;
      }
      // For other errors, log and try to generate bundle
      debug.error('Error fetching prekey bundle data:', userFetchError);
      await uploadPrekeyBundle(userId, identityKeyPair);
      return;
    }
    
    // If no bundle exists, generate one
    if (!userData || !userData.prekey_bundle_updated_at || !userData.signed_prekey || userData.signed_prekey_id === null || !userData.signed_prekey_signature) {
      debug.log(`Generating prekey bundle for user ${userId}`);
      await uploadPrekeyBundle(userId, identityKeyPair);
      return;
    }
    
    // Check how many unused prekeys remain
    const { count: prekeyCount, error: countError } = await supabase
      .from('prekeys')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('used_at', null);
    
    if (countError) {
      debug.error('Error counting unused prekeys:', countError);
      throw countError;
    }
    
    const unusedCount = prekeyCount ?? 0;
    
    if (unusedCount < 10) {
      // Regenerate if less than 10 remaining
      debug.log(`Regenerating prekey bundle for user ${userId} (${unusedCount} remaining)`);
      await uploadPrekeyBundle(userId, identityKeyPair);
      return;
    }
    
    // Check if bundle is old (30 days)
    const bundleAge = Date.now() - new Date(userData.prekey_bundle_updated_at).getTime();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    
    if (bundleAge > thirtyDays) {
      debug.log(`Regenerating prekey bundle for user ${userId} (bundle is ${Math.floor(bundleAge / (24 * 60 * 60 * 1000))} days old)`);
      await uploadPrekeyBundle(userId, identityKeyPair);
      return;
    }
    
    debug.log(`Prekey bundle for user ${userId} is up to date (${unusedCount} unused prekeys)`);
  } catch (error) {
    debug.error('Error ensuring prekey bundle:', error);
    throw error;
  }
}

